import React, { useEffect, useMemo } from 'react';
import { Fragment, ReactNode, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  BellIcon,
  BriefcaseIcon,
  CalendarIcon,
  HomeIcon,
  ClockIcon,
  DocumentTextIcon,
  UserIcon,
  UsersIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  ChatBubbleBottomCenterTextIcon,
  CreditCardIcon,
  BanknotesIcon,
  MagnifyingGlassIcon,
  RectangleStackIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';

import { fill } from '@cloudinary/url-gen/actions/resize';
import { CloudinaryImage } from '@cloudinary/url-gen';
import { SidebarLayout } from '../../component/sidebar-layout';
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
} from '../../component/sidebar';

import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../../component/dropdown';
import { Icon } from '@tremor/react';
import { useClerk, useUser } from '@clerk/clerk-react';
import axios from 'axios';
import { useQuery } from 'react-query';
import configManagement from '../../../network/configManagement';
import { dismissNotification } from '../../../network';
import { brandingService } from '../../../services/branding';
import { getWorkspaceLogo } from '../../../network';
import Notifications from './components/Notifications';
import { useChatbotModal } from '../../../hooks/useChatbotModal';
import ChatbotModal from './components/Chatbot';
import { CommandPalette } from './components/CommandPalette';
import { useCurrentUserRole } from '../../../hooks/useCurrentUserRole';
import { hasRoleLevel, RoleLevel } from '../../../utils/roles';

const myImage = new CloudinaryImage('redstone-icon_xwvsrc', {
  cloudName: 'dnzgb3sfo',
}).resize(fill().width(300).height(237));

const notificationContent = (notification: any): string | null => {
  if (notification?.content) return notification.content;
  const start = notification?.time_range?.start;
  const end = notification?.time_range?.end;
  if (start && end) return start === end ? `Leave (${start})` : `Leave (${start} - ${end})`;
  return null;
};

const buildNotification = (notifications: any) => {
  return notifications.map((notification: any) => {
    return {
      ...notification,
      id: notification.id, // Ensure ID is included
      user: { full_name: notification?.name },
      message: notification?.description,
      // The API sends the line to show under the title (a leave request's
      // dates); other notification types have none.
      content: notificationContent(notification),
      time: notification?.time,
      read: notification?.read ?? false,
    };
  });
};

type Props = {
  children: ReactNode;
};

const { REACT_APP_API_HOST } = process.env;

type NavigationItem = {
  id: string;
  label: string;
  icon: any;
  href: string;
  alwaysEnabled?: boolean;
  /** Lowest role level that sees the item (see RequireRole routes). */
  minLevel?: RoleLevel;
};

// Define all potential navigation items locally
const allNavigationItems: NavigationItem[] = [
  { id: 'home', label: 'Home', icon: HomeIcon, href: '/', alwaysEnabled: true },
  { id: 'calendar', label: 'Calendar', icon: CalendarIcon, href: '/calendar' },
  { id: 'timeoff', label: 'Time Off', icon: ClockIcon, href: '/time-off' },
  {
    id: 'people_management',
    label: 'People',
    icon: UsersIcon,
    href: '/people',
    minLevel: 'admin',
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: ChatBubbleBottomCenterTextIcon,
    href: '/reports',
    minLevel: 'manager',
  },
  {
    id: 'ats',
    label: 'Applicant Tracking',
    icon: BriefcaseIcon,
    href: '/ats',
    minLevel: 'manager',
  },
  {
    id: 'timesheets',
    label: 'Timesheets',
    icon: TableCellsIcon,
    href: '/timesheets',
  },
  {
    id: 'document_management',
    label: 'Document Management',
    icon: DocumentTextIcon,
    href: '/document-management',
  },
  {
    id: 'rota_management',
    label: 'Rota Management',
    icon: RectangleStackIcon,
    href: '/rota-management',
  },
];

const isUuidLike = (value?: string) =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );

export function ControlPanelLayout({ children }: Props) {
  const [navigation, setNavigation] = useState<
    {
      id: string;
      href: string;
      label: string;
      icon: any;
      enabled: boolean;
      minLevel?: RoleLevel;
    }[]
  >([]);
  const { user } = useUser();
  const { level: roleLevel } = useCurrentUserRole();
  const canSee = (minLevel?: RoleLevel) =>
    !minLevel || hasRoleLevel(roleLevel, minLevel);
  const [url, setUrl] = useState('');
  const [companyName, setCompanyName] = useState('Redstone HR');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const { signOut } = useClerk();

  const { isChatbotModalOpen, closeChatbot, openChatbot } = useChatbotModal();
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [newNotifications, setNewNotifications] = useState<any[]>([]); // Specify type
  const [isShownNotification, setIsShownNotification] = useState(false);

  const { data: value, status } = useQuery({
    queryKey: ['billingPortalUrl'],
    queryFn: async () => {
      const response = await axios.get(
        `${REACT_APP_API_HOST}/v1/profile/billing`,
      );
      return response.data;
    },
    retry: false,
  });

  const { data: response, status: responseStatus } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await axios.get(`${REACT_APP_API_HOST}/v1/notification`);
      return response.data;
    },
    refetchInterval: 30000,
  });

  const { data: brandingResponse } = useQuery(
    ['branding-assets'],
    brandingService.getBranding,
  );

  const { data: configResponse } = useQuery(
    'config',
    configManagement.getConfig,
  );

  const { data: workspaceResponse } = useQuery({
    queryKey: ['sidebar-workspace'],
    queryFn: async () => {
      const response = await axios.get(`${REACT_APP_API_HOST}/v1/workspace`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: false,
  });

  const { data: workspaceLogo } = useQuery(
    ['workspace-logo'],
    getWorkspaceLogo,
  );

  useEffect(() => {
    if (responseStatus === 'success' && response?.notifications) {
      setNewNotifications(buildNotification(response.notifications));
    }
  }, [responseStatus, response]);

  useEffect(() => {
    const brandingName = brandingResponse?.data?.company?.name;
    const configName = configResponse?.data?.data?.company?.name;
    const workspaceName = workspaceResponse?.data?.general?.companyName;
    const resolvedCompanyName = [workspaceName, configName, brandingName].find(
      (value) =>
        typeof value === 'string' &&
        value.trim().length > 0 &&
        !isUuidLike(value.trim()),
    );

    if (resolvedCompanyName) {
      setCompanyName(resolvedCompanyName);
    } else {
      setCompanyName('Redstone HR');
    }
    if (brandingResponse?.data?.branding) {
      const { primaryColor, secondaryColor, accentColor } =
        brandingResponse.data.branding;
      const root = document.documentElement;
      root.style.setProperty('--brand-primary', primaryColor);
      root.style.setProperty('--brand-secondary', secondaryColor);
      root.style.setProperty('--brand-accent', accentColor);
    }
  }, [brandingResponse, configResponse, workspaceResponse]);

  useEffect(() => {
    if (!workspaceLogo) {
      setLogoUrl(null);
      return;
    }
    const url = URL.createObjectURL(workspaceLogo);
    setLogoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [workspaceLogo]);

  useEffect(() => {
    if (status === 'success') {
      setUrl(value?.data?.billing ?? '');
    }
  }, [status, value]);

  const userNavigation = useMemo(
    () =>
      [
        {
          name: 'Your profile',
          href: '/profile',
          icon: UserIcon,
          external: false,
        },
        {
          name: 'Settings',
          href: '/settings',
          icon: Cog6ToothIcon,
          external: false,
          minLevel: 'admin' as RoleLevel,
        },
        {
          name: 'Organization',
          href: '/organization',
          icon: BanknotesIcon,
          external: false,
          minLevel: 'admin' as RoleLevel,
        },
        url
          ? { name: 'Billing', href: url, icon: CreditCardIcon, external: true }
          : null,
        {
          name: 'Sign out',
          href: '/signout',
          onClick: () => {
            signOut();
          },
          icon: ArrowRightOnRectangleIcon,
          external: false,
        },
      ]
        .filter((item): item is Exclude<typeof item, null> => item !== null)
        .filter(
          (item) => !item.minLevel || hasRoleLevel(roleLevel, item.minLevel),
        ),
    [url, signOut, roleLevel], // Added signOut dependency
  );

  // Handler function to remove a notification by its ID
  const handleClearNotification = async (idToClear: string | number) => {
    setNewNotifications((currentNotifications) => {
      // Use filter to create a new array excluding the item with the matching ID
      const updatedNotifications = currentNotifications.filter(
        (notification) => notification.id !== idToClear,
      );

      // If the length hasn't changed, the ID wasn't found
      if (updatedNotifications.length === currentNotifications.length) {
        console.warn(
          '[ControlPanelLayout] Notification ID not found for removal:',
          idToClear,
        );
      }

      return updatedNotifications;
    });
    try {
      await dismissNotification(idToClear);
    } catch (error) {
      console.warn('Failed to dismiss notification on server', error);
    }
  };

  useEffect(() => {
    try {
      const enabledFeaturesFromAPI = configResponse?.data?.data?.features || [];
      const enabledFeatureIds = new Set(
        enabledFeaturesFromAPI
          .filter((f: any) => f.enabled === true)
          .map((f: any) => f.id),
      );

      const processedNavigation = allNavigationItems.map((item) => ({
        ...item,
        enabled: item.alwaysEnabled || enabledFeatureIds.has(item.id),
        label:
          enabledFeaturesFromAPI.find((f: any) => f.id === item.id)?.label ||
          item.label,
      }));

      setNavigation(processedNavigation);
    } catch (error) {
      const fallbackNavigation = allNavigationItems.map((item) => ({
        ...item,
        enabled: !!item.alwaysEnabled,
      }));
      setNavigation(fallbackNavigation);
    }
  }, [configResponse]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsCommandPaletteOpen((current) => !current);
      }
      if (event.key === 'Escape') {
        setIsCommandPaletteOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <SidebarLayout
        navbar={<></>}
        sidebar={
          <Sidebar>
            <SidebarHeader>
              <div className="flex flex-shrink-0 items-center">
                <img
                  className="h-8 w-auto "
                  src={logoUrl || myImage.toURL()}
                  alt="logo"
                />
                <SidebarLabel className="ml-2 font-medium">
                  {companyName}
                </SidebarLabel>
              </div>
            </SidebarHeader>
            <SidebarBody className="flex justify-between">
              <SidebarSection className="space-y-1">
                {navigation?.map((item) =>
                  item?.enabled && canSee(item.minLevel) ? ( // Now just check the processed 'enabled' status
                    <NavLink key={item.id} to={item.href}>
                      <SidebarItem>
                        <Icon icon={item.icon} color="gray" />
                        <SidebarLabel>{item.label}</SidebarLabel>
                      </SidebarItem>
                    </NavLink>
                  ) : null,
                )}
              </SidebarSection>
              <SidebarSection>
                <SidebarItem
                  className="flex w-[100%] flex-row items-center justify-between"
                  onClick={() => setIsCommandPaletteOpen(true)}
                >
                  <div className="flex items-center">
                    <Icon icon={MagnifyingGlassIcon} color="gray" size="md" />
                    Search
                  </div>
                  <div className="text-xs text-gray-400">Ctrl+K</div>
                </SidebarItem>
                <SidebarItem
                  className="flex w-[100%] flex-row items-center justify-between "
                  onClick={() => {
                    setIsShownNotification(true);
                  }}
                >
                  <div className="flex items-center">
                    <Icon icon={BellIcon} color="gray" size="md" />
                    Notifications
                  </div>
                  <div>
                    <p className="rounded-lg bg-[#FEE2E2] px-2 text-[#DC2626]">
                      {newNotifications?.length || 0}
                    </p>
                  </div>
                </SidebarItem>
              </SidebarSection>
            </SidebarBody>
            <SidebarFooter>
              <Dropdown>
                <DropdownButton as={SidebarItem}>
                  <span className="sr-only">Open user menu</span>
                  <img
                    className="h-8 w-8 rounded-full "
                    src={user?.imageUrl as string}
                    alt="User icon"
                  />
                  <div className="truncate">
                    <p className="text-sm text-tremor-content">
                      {user?.firstName}
                    </p>
                    <p className="text-[12px] text-tremor-content-subtle ">
                      {user?.primaryEmailAddress?.emailAddress}
                    </p>
                  </div>
                </DropdownButton>
                <DropdownMenu>
                  {userNavigation.map((item, index) => (
                    <React.Fragment key={item.name}>
                      <DropdownItem className="flex cursor-pointer justify-center">
                        {item.external ? (
                          <a
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex flex-row items-center space-x-2"
                            onClick={item.onClick}
                          >
                            <Icon icon={item.icon} color="gray" />
                            <DropdownLabel>{item.name}</DropdownLabel>
                          </a>
                        ) : (
                          <NavLink
                            to={item.href}
                            className="flex flex-row items-center space-x-2"
                            onClick={item.onClick}
                          >
                            <Icon icon={item.icon} color="gray" />
                            <DropdownLabel>{item.name}</DropdownLabel>
                          </NavLink>
                        )}
                      </DropdownItem>
                      {index < 2 && <DropdownDivider />}
                    </React.Fragment>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </SidebarFooter>
          </Sidebar>
        }
      >
        <Notifications
          isOpen={isShownNotification}
          setOpen={setIsShownNotification}
          notifications={newNotifications}
          onClearNotification={handleClearNotification} // Pass the handler function
        />
        <CommandPalette
          roleLevel={roleLevel}
          isOpen={isCommandPaletteOpen}
          onClose={() => setIsCommandPaletteOpen(false)}
        />
        <ChatbotModal isOpen={isChatbotModalOpen} setOpen={closeChatbot} />
        <main>
          <div className="mx-auto w-full ">{children}</div>
        </main>
      </SidebarLayout>
      <button
        type="button"
        onClick={openChatbot}
        className="fixed bottom-6 right-6 z-40 rounded-full bg-[#DC2626] px-4 py-3 text-sm font-semibold text-white shadow-lg"
      >
        Ask HR
      </button>
    </>
  );
}
